import styled from 'styled-components'
import { Ratings, theme } from '@notino/react-styleguide'
import { BREAKPOINTS } from '../../../../styles/constants'
import { screenReaderOnly } from '../../../../styles/helpers'

export const SalonReviewsContainer = styled.section`
	@media (min-width: ${BREAKPOINTS.lg}) {
		margin-top: 4rem;
	}
`

export const ReviewsHeader = styled.div`
	display: flex;
	flex-direction: column;

	@media (min-width: ${BREAKPOINTS.md}) {
		gap: 2.5rem;
	}
`

export const ReviewsHeaderTop = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		flex-direction: row;
		gap: 2.5rem;
	}
`

export const HeaderLeftColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	flex-shrink: 0;
	text-align: center;
	padding: 0 2rem;
`

export const Heading = styled.h2`
	${screenReaderOnly};
	margin: 0;

	@media (min-width: ${BREAKPOINTS.lg}) {
		position: static;
		width: auto;
		height: auto;
		clip: auto;
		clip-path: none;
		padding: 0;
		border: 0;
		overflow: visible;
		white-space: normal;
		${theme.typography.titleDisplay};
		margin-bottom: 1.25rem;
	}
`

export const AverageRatingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	text-align: center;
`

/** Wraps the average-rating heading + summary so the SR-only h3 is not a flex item in HeaderLeftColumn. */
export const AverageRatingBlock = styled.div``

export const AverageRatingValue = styled.span`
	${theme.typography.titleLarge};

	@media (min-width: ${BREAKPOINTS.md}) {
		${theme.typography.titleDisplay};
	}
`

export const TotalRatingsCount = styled.span`
	${theme.typography.bodySmall};
	color: ${theme.color.text.secondary};
	display: flex;
	align-items: center;
	gap: 4px;
`

export const GroupedRatingsContainer = styled.div`
	width: 100%;

	@media (min-width: ${BREAKPOINTS.md}) {
		width: 40%;
	}
`

export const PartialRatingsContainer = styled.div`
	width: 100%;
	padding-top: 1.5rem;
	border-top: 1px solid ${theme.color.border.divider};
	margin-top: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		padding: 0;
		border-top: none;
		margin-top: 0;
	}
`

export const AddReviewsButtonDesktop = styled.div`
	display: none;

	@media (min-width: ${BREAKPOINTS.md}) {
		display: block;
	}
`

export const AddReviewsButtonMobile = styled.div`
	margin-top: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		display: none;
	}
`

export const ReviewsContainer = styled.div`
	border-top: 1px solid ${theme.color.border.divider};
	margin-top: 2.5rem;
`

export const ReviewContainer = styled.article`
	border-bottom: 1px solid ${theme.color.border.divider};
	padding: 2rem 0;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`

export const Review = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		flex-direction: row;
		gap: 2rem;
	}
`

export const ReviewMessage = styled.div`
	${theme.typography.bodyLarge};
	white-space: pre-wrap;
`

export const ReviewInfo = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 0.5rem;
	width: 100%;

	@media (min-width: ${BREAKPOINTS.md}) {
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		width: 240px;
		flex-shrink: 0;
	}
`

export const ReviewInfoLeftContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;

	svg {
		display: block;
	}
`

export const StyledRatings = styled(Ratings)`
	margin-right: 5px;
	display: inline-flex;

	svg {
		display: block;
	}
`

export const ReviewerName = styled.h4`
	/* resetting global notino margin on heading */
	margin: 0;
	${theme.typography.labelRegular};
	word-break: break-word;
`

export const VerifiedReview = styled.div`
	${theme.typography.labelSmall};
	color: ${theme.color.text.positive};
	display: flex;
	align-items: center;
	gap: 4px;

	svg {
		flex-shrink: 0;
	}
`

export const VerifiedReviewTooltipContent = styled.div`
	text-align: center;
`

export const ReviewDate = styled.time`
	${theme.typography.labelRegular400};
	color: ${theme.color.text.secondary};
	white-space: nowrap;
`

export const ReviewsButtonContainer = styled.div`
	margin-top: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		margin-top: 2.5rem;
	}
`

export const ReviewResponse = styled.div`
	border-left: 1px solid ${theme.color.border.divider};
	padding-left: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;

	@media (min-width: ${BREAKPOINTS.md}) {
		flex-direction: row;
		gap: 2rem;
	}
`

export const ReviewResponseInfo = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 0.5rem;
	width: 100%;

	@media (min-width: ${BREAKPOINTS.md}) {
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		width: calc(240px - 1rem);
		flex-shrink: 0;
	}
`

export const SalonName = styled.div`
	${theme.typography.labelRegular};
	word-break: break-word;
`

export const ReviewResponseDate = styled.time`
	${theme.typography.labelRegular400};
	color: ${theme.color.text.secondary};
`

export const ResponseMessage = styled.div`
	${theme.typography.bodyLarge};
	white-space: pre-wrap;
`
